import { ActionIcon, Group } from "@mantine/core";
import { IconChartHistogram } from "@tabler/icons-react";
import * as _ from "lodash-es";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { generatePath, Link, useParams } from "react-router";
import { getDefaultMRTOptions } from "../../../../../../mantine/getDefaultMRTOptions";
import { ROUTES } from "../../../../../../routes";
import { roundedNumericFormatter } from "../../../../../../utils/RoundedNumberFormat";
import { TABS } from "../../../EnergyDataView";
import { METER_ID_PREFIX } from "../../../utils/getMeterIdFromSelectedId";
import type { MeterDataAvailability } from "../../EnergyDataAvailability.types";

interface MeterTableProps {
  meterData: Array<MeterDataAvailability>;
  firstDateIso: string;
  lastDateIso: string;
}

function MeterTable({ meterData, firstDateIso, lastDateIso }: MeterTableProps) {
  const { projectId } = useParams();

  const tableData = useMemo<Array<MeterDataAvailability>>(
    () => meterData,
    [meterData]
  );

  const columns = useMemo<MRT_ColumnDef<MeterDataAvailability>[]>(
    () => [
      {
        header: "Name",
        accessorKey: "meter_name",
        Cell: ({ cell, row }) => {
          const meterName = cell.getValue<string>();
          const meterId = row.original.meter_id;

          return (
            <Group justify="space-between" w="100%">
              {meterName}
              {projectId && (
                <ActionIcon
                  component={Link}
                  size="sm"
                  to={{
                    pathname: generatePath(
                      `${ROUTES.energyData.entry}/${TABS.TAB_RAW_ENERGY_DATA}`,
                      { projectId }
                    ),
                    search: `?firstDate=${firstDateIso}&lastDate=${lastDateIso}&selectedId=${METER_ID_PREFIX}${meterId}`
                  }}
                  variant="transparent"
                >
                  <IconChartHistogram />
                </ActionIcon>
              )}
            </Group>
          );
        }
      },
      {
        header: "Zählernummer",
        accessorKey: "meter_number"
      },
      {
        header: "Einspeisung",
        accessorKey: "feedin_percentage",
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>()?.toString() ?? "";
          return roundedNumericFormatter(value, {
            decimalScale: 2,
            decimalSeparator: ",",
            thousandSeparator: ".",
            suffix: " %"
          });
        },
        size: 150,
        grow: false,
        mantineTableHeadCellProps: {
          align: "right"
        },
        mantineTableBodyCellProps: {
          align: "right"
        }
      },
      {
        header: "Entnahme",
        accessorKey: "feedout_percentage",
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>()?.toString() ?? "";
          return roundedNumericFormatter(value, {
            decimalScale: 2,
            decimalSeparator: ",",
            thousandSeparator: ".",
            suffix: " %"
          });
        },
        size: 150,
        grow: false,
        mantineTableHeadCellProps: {
          align: "right"
        },
        mantineTableBodyCellProps: {
          align: "right"
        }
      }
    ],
    [firstDateIso, lastDateIso, projectId]
  );

  const defaultOptions = getDefaultMRTOptions<MeterDataAvailability>({
    hideBorder: true
  });

  const tableOptions = _.merge({}, defaultOptions, {
    layoutMode: "grid",
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false
  });

  const table = useMantineReactTable<MeterDataAvailability>({
    ...tableOptions,
    columns,
    data: tableData
  });

  return <MantineReactTable table={table} />;
}

export { MeterTable };
